/**
 * V2AdminDeliveryPage — Stage 3 delivery / project tracker.
 *
 * Routes:
 *   /admin/os/projects                    → list of every won job
 *   /admin/os/projects/:projectId         → detail with task list, status, progress
 *
 * INTERNAL only. Sits behind the existing admin JWT (same as the rest of
 * the OS). NO client-facing surface in this round — concerns and upgrade
 * requests render as placeholders so Michael can see the seam, but they're
 * not yet connected to any client submission UI.
 *
 * Data model: see /app/backend/integrations/delivery_engine.py.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { V2OSChrome, V2OSC as C, V2OSTokenStore as tokenStore, v2OSAuth as auth } from './V2AdminOSPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Domain constants (must mirror backend delivery_engine.py) ────────────
const PROJECT_STATUSES = ['awaiting_kickoff', 'kickoff', 'in_build', 'delivered', 'on_hold'];
const TASK_STATUSES = ['todo', 'doing', 'done'];

const STATUS_META = {
  awaiting_kickoff: { label: 'Awaiting kickoff', color: '#9156C4', dot: '#9156C4' },
  kickoff:          { label: 'Kickoff',          color: '#4F8FD0', dot: '#4F8FD0' },
  in_build:         { label: 'In build',         color: '#1FB6C1', dot: '#1FB6C1' },
  delivered:        { label: 'Delivered',        color: '#34D1B0', dot: '#34D1B0' },
  on_hold:          { label: 'On hold',          color: '#EB258D', dot: '#EB258D' },
};

const TASK_STATUS_META = {
  todo:  { label: 'To do',  color: 'rgba(220,240,245,0.55)' },
  doing: { label: 'Doing',  color: '#4F8FD0' },
  done:  { label: 'Done',   color: '#34D1B0' },
};

const fmt$ = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};

// ─── Small UI atoms ────────────────────────────────────────────────────────
const StatusPill = ({ status, size = 'md' }) => {
  const meta = STATUS_META[status] || { label: status || '—', color: C.faint, dot: C.faint };
  const padding = size === 'sm' ? '4px 10px' : '6px 12px';
  const fontSize = size === 'sm' ? 11 : 12;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding,
      borderRadius: 999, fontFamily: 'Inter', fontSize, fontWeight: 600,
      color: meta.color, background: `${meta.color}1A`, border: `1px solid ${meta.color}55`,
      letterSpacing: '0.02em',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: meta.dot }} />
      {meta.label}
    </span>
  );
};

const ProgressBar = ({ pct }) => (
  <div style={{ height: 8, background: 'rgba(220,240,245,0.08)', borderRadius: 999, overflow: 'hidden', position: 'relative' }}>
    <div style={{ width: `${pct}%`, height: '100%', background: C.spectrum, transition: 'width 280ms ease' }} />
  </div>
);

// ─── LIST VIEW ──────────────────────────────────────────────────────────────
export const V2AdminDeliveryListPage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('active'); // active | all | <status>
  const [xeroStatus, setXeroStatus] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!tokenStore.get()) return;
    let cancelled = false;
    (async () => {
      try {
        const [proj, xero] = await Promise.all([
          axios.get(`${API}/admin/os/projects`, auth()),
          axios.get(`${API}/admin/xero/status`, auth()).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setData(proj.data);
        setXeroStatus(xero.data);
      } catch (e) {
        if (e?.response?.status === 401) { tokenStore.clear(); const r=encodeURIComponent(window.location.pathname+window.location.search); window.location.assign('/admin/login?returnTo='+r); return; }
        setErr(e?.response?.data?.detail || 'Failed to load projects.');
      }
    })();
    return () => { cancelled = true; };
  }, [nav]);

  const connectXero = async () => {
    try {
      const r = await axios.get(`${API}/admin/xero/start`, auth());
      window.location.href = r.data.authorization_url;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Connect Xero failed.');
    }
  };

  const projects = data?.projects || [];
  const counts = data?.counts || {};

  const filtered = useMemo(() => {
    if (filter === 'all') return projects;
    if (filter === 'active') return projects.filter((p) => p.status !== 'delivered');
    return projects.filter((p) => p.status === filter);
  }, [projects, filter]);

  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;

  return (
    <V2OSChrome activeTab="delivery">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Kindra OS · Execution</p>
          <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, marginTop: 6 }} data-testid="delivery-h1">
            Execution
          </h1>
          <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 14, marginTop: 8, maxWidth: 580 }}>
            Deliver finished work to your clients. Every accepted quote becomes a project here — move it through Kickoff → In build → Delivered, knock off tasks as you go.
          </p>
        </div>
        <XeroStatusBadge status={xeroStatus} onConnect={connectXero} />
      </div>

      {/* KPI tiles */}
      <div className="grid gap-3 mb-7" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {[
          { key: 'awaiting_kickoff', label: 'Awaiting kickoff' },
          { key: 'kickoff',          label: 'Kickoff' },
          { key: 'in_build',         label: 'In build' },
          { key: 'on_hold',          label: 'On hold' },
          { key: 'delivered',        label: 'Delivered' },
        ].map(({ key, label }) => {
          const meta = STATUS_META[key];
          return (
            <button
              key={key}
              data-testid={`delivery-kpi-${key}`}
              onClick={() => setFilter(filter === key ? 'active' : key)}
              style={{
                padding: '14px 16px', borderRadius: 16, textAlign: 'left',
                background: filter === key ? 'rgba(220,240,245,0.06)' : 'rgba(220,240,245,0.03)',
                border: `1px solid ${filter === key ? meta.color + '66' : C.hair}`, cursor: 'pointer',
                color: C.icy, fontFamily: 'Inter',
              }}>
              <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: meta.color, fontWeight: 700 }}>{label}</div>
              <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 28, marginTop: 4 }}>{counts[key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { k: 'active', l: 'Active' },
          { k: 'all',    l: `All (${counts.total ?? 0})` },
        ].map(({ k, l }) => (
          <button key={k} data-testid={`delivery-filter-${k}`} onClick={() => setFilter(k)}
                  style={{
                    padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                    background: filter === k ? 'rgba(220,240,245,0.10)' : 'transparent',
                    color: filter === k ? C.icy : C.muted,
                    border: `1px solid ${filter === k ? C.hairBright : C.hair}`,
                    fontFamily: 'Inter', fontSize: 13, fontWeight: 500, letterSpacing: '0.02em',
                  }}>
            {l}
          </button>
        ))}
      </div>

      {/* Project list */}
      {err && <div style={{ color: '#EB258D', fontFamily: 'Inter', fontSize: 13, marginBottom: 14 }}>{err}</div>}
      {!data && !err && <div style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</div>}

      {data && filtered.length === 0 && (
        <div data-testid="delivery-empty" style={{
          padding: '40px 28px', borderRadius: 18, background: C.surface,
          border: `1px solid ${C.hair}`, fontFamily: 'Inter', color: C.muted, lineHeight: 1.6, fontSize: 14,
        }}>
          {filter === 'active' && counts.total === 0
            ? 'No won jobs yet. The moment a client accepts a quote, the project shows up here.'
            : `No projects match "${filter}".`}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.map((p) => (
          <Link key={p.project_id} to={`/admin/os/projects/${p.project_id}`}
                data-testid={`delivery-row-${p.project_id}`}
                style={{
                  display: 'block', padding: '18px 22px', borderRadius: 16,
                  background: C.surface, border: `1px solid ${C.hair}`,
                  textDecoration: 'none', color: C.icy,
                }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, lineHeight: 1.2, color: C.icy }}>
                    {p.business_name}
                  </h3>
                  <StatusPill status={p.status} size="sm" />
                  {p.open_concerns > 0 && (
                    <span style={{ fontFamily: 'Inter', fontSize: 11, color: '#EB258D', fontWeight: 600 }}>
                      {p.open_concerns} open concern{p.open_concerns !== 1 ? 's' : ''}
                    </span>
                  )}
                  {p.pending_upgrades > 0 && (
                    <span style={{ fontFamily: 'Inter', fontSize: 11, color: '#34D1B0', fontWeight: 600 }}>
                      {p.pending_upgrades} upgrade ask{p.pending_upgrades !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-5 flex-wrap" style={{ color: C.muted, fontSize: 12.5, fontFamily: 'Inter' }}>
                  <span>{p.contact_name || '—'}</span>
                  <span>Accepted {fmtDate(p.accepted_at)}</span>
                  <span>{fmt$(p.total_aud)} {p.currency}</span>
                  <span style={{ color: p.payment_status === 'paid' ? '#34D1B0' : C.faint }}>
                    Payment: {p.payment_status}
                  </span>
                </div>
              </div>
              <div style={{ minWidth: 180, textAlign: 'right' }}>
                <div style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                  {p.task_counts.done}/{p.task_counts.total} tasks · {p.progress_pct}%
                </div>
                <ProgressBar pct={p.progress_pct} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </V2OSChrome>
  );
};

// ─── DETAIL VIEW ────────────────────────────────────────────────────────────
export const V2AdminDeliveryDetailPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [err, setErr] = useState('');
  const [newTask, setNewTask] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/os/projects/${projectId}`, auth());
      setProject(r.data.project);
    } catch (e) {
      if (e?.response?.status === 401) { tokenStore.clear(); nav('/admin/login'); return; }
      setErr(e?.response?.data?.detail || 'Failed to load project.');
    }
  };
  useEffect(() => { if (tokenStore.get()) refresh(); /* eslint-disable-next-line */ }, [projectId]);

  const setStatus = async (status) => {
    if (!project || busy) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/os/projects/${projectId}/status`, { status }, auth());
      setProject(r.data.project);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Status update failed.');
    } finally { setBusy(false); }
  };

  const setTaskStatus = async (taskId, status) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/os/projects/${projectId}/tasks/${taskId}`, { status }, auth());
      setProject(r.data.project);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Task update failed.');
    } finally { setBusy(false); }
  };

  const addTask = async () => {
    const title = newTask.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/os/projects/${projectId}/tasks`, { title }, auth());
      setProject(r.data.project);
      setNewTask('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Add task failed.');
    } finally { setBusy(false); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/os/projects/${projectId}/tasks/${taskId}`, auth());
      setProject(r.data.project);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed.');
    } finally { setBusy(false); }
  };

  const xeroSync = async () => {
    if (busy) return;
    const labelAsTest = window.confirm(
      'Push DRAFT invoice to Xero for this project?\n\n' +
      'Click OK to label this as a TEST draft (prefixes the Xero reference with "TEST — ") — recommended for the first end-to-end run.\n' +
      'Click Cancel to push as a normal draft.\n\n' +
      'Either way the invoice will be DRAFT in Xero. We never auto-send.'
    );
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/os/projects/${projectId}/xero_sync${labelAsTest ? '?test=1' : ''}`,
        {}, auth(),
      );
      if (r.data.project) setProject(r.data.project);
      else await refresh();
      alert(`DRAFT invoice ${r.data.invoice_number || 'created'} in Xero. Status: ${r.data.invoice_status || 'DRAFT'}.\n\nOpen Xero to review and send. We never auto-send.`);
    } catch (e) {
      alert(`Xero sync failed: ${e?.response?.data?.detail || 'unknown error'}`);
    } finally { setBusy(false); }
  };

  const xeroRefresh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/os/projects/${projectId}/xero_refresh`, {}, auth());
      if (r.data.project) setProject(r.data.project);
    } catch (e) {
      alert(`Refresh failed: ${e?.response?.data?.detail || 'unknown error'}`);
    } finally { setBusy(false); }
  };

  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;
  if (err) return <V2OSChrome activeTab="delivery"><div data-testid="delivery-detail-error"
        style={{ padding: '40px 28px', borderRadius: 18, background: C.surface, border: `1px solid ${C.hair}`, color: C.icy, fontFamily: 'Inter' }}>
    <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 22, marginBottom: 8 }}>Couldn&apos;t open this project</h2>
    <p style={{ color: C.muted, fontSize: 14, marginBottom: 18 }}>{err}</p>
    <Link to="/admin/os/projects" style={{ display: 'inline-block', padding: '10px 18px', borderRadius: 999,
        border: `1px solid ${C.hairBright || 'rgba(220,240,245,0.20)'}`, color: C.icy, fontWeight: 600, textDecoration: 'none' }}>
      ← Back to Execution
    </Link>
  </div></V2OSChrome>;
  if (!project) return <V2OSChrome activeTab="delivery"><div style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</div></V2OSChrome>;

  const tc = project.task_counts || { total: 0, todo: 0, doing: 0, done: 0 };

  return (
    <V2OSChrome activeTab="delivery"
                crumb={<><Link to="/admin/os/projects" style={{ color: C.muted, textDecoration: 'none' }}>Execution</Link>
                <span style={{ margin: '0 8px' }}>/</span> {project.business_name}</>}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p style={{ fontFamily: 'Inter', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Project · Accepted {fmtDate(project.accepted_at)}
          </p>
          <h1 data-testid="delivery-detail-h1" style={{ fontFamily: 'Poppins', fontWeight: 600, color: C.icy, fontSize: 'clamp(26px,3.4vw,34px)', lineHeight: 1.15, marginTop: 6 }}>
            {project.business_name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-5" style={{ color: C.muted, fontFamily: 'Inter', fontSize: 13 }}>
            <span>{project.contact_name || '—'}</span>
            <span>{fmt$(project.total_aud)} {project.currency}</span>
            <span style={{ color: project.payment_status === 'paid' ? '#34D1B0' : C.faint }}>
              Payment: {project.payment_status} <span style={{ color: C.faint, fontSize: 11 }}>(Xero sync pending)</span>
            </span>
          </div>
        </div>
      </div>

      {/* Status selector + progress */}
      <div className="rounded-2xl mb-7" style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '20px 22px' }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Status</span>
            <StatusPill status={project.status} />
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: C.muted }}>
            {tc.done}/{tc.total} tasks done · {project.progress_pct}%
          </div>
        </div>
        <ProgressBar pct={project.progress_pct} />
        <div className="flex flex-wrap gap-2 mt-5">
          {PROJECT_STATUSES.map((s) => (
            <button key={s} data-testid={`delivery-set-status-${s}`}
                    disabled={busy || s === project.status}
                    onClick={() => setStatus(s)}
                    style={{
                      padding: '7px 14px', borderRadius: 999,
                      background: s === project.status ? `${STATUS_META[s].color}22` : 'transparent',
                      color: s === project.status ? STATUS_META[s].color : C.muted,
                      border: `1px solid ${s === project.status ? STATUS_META[s].color + '55' : C.hair}`,
                      fontFamily: 'Inter', fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
                      cursor: s === project.status ? 'default' : 'pointer',
                      opacity: busy && s !== project.status ? 0.5 : 1,
                    }}>
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Xero invoice card — DRAFT only, payment status read-back */}
      <XeroInvoiceCard project={project} busy={busy} onSync={xeroSync} onRefresh={xeroRefresh} />

      {/* Tasks */}
      <section className="rounded-2xl mb-6" style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '22px 24px' }}>
        <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: C.icy, marginBottom: 14 }}>
          Tasks
          <span style={{ marginLeft: 10, fontFamily: 'Inter', fontWeight: 400, fontSize: 13, color: C.faint }}>
            {tc.todo} to do · {tc.doing} in progress · {tc.done} done
          </span>
        </h2>

        <div className="flex flex-col gap-2">
          {(project.tasks || []).map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`}
                 style={{
                   display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 16, alignItems: 'center',
                   padding: '12px 14px', borderRadius: 12, border: `1px solid ${C.hair}`,
                   background: t.status === 'done' ? 'rgba(52,209,176,0.04)' : 'rgba(220,240,245,0.02)',
                 }}>
              <div className="flex gap-1">
                {TASK_STATUSES.map((s) => (
                  <button key={s} data-testid={`task-${t.id}-set-${s}`}
                          onClick={() => setTaskStatus(t.id, s)}
                          disabled={busy || t.status === s}
                          title={TASK_STATUS_META[s].label}
                          style={{
                            padding: '4px 9px', borderRadius: 999,
                            background: t.status === s ? `${TASK_STATUS_META[s].color}22` : 'transparent',
                            color: t.status === s ? TASK_STATUS_META[s].color : C.faint,
                            border: `1px solid ${t.status === s ? TASK_STATUS_META[s].color + '66' : C.hair}`,
                            fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', cursor: t.status === s ? 'default' : 'pointer',
                          }}>
                    {TASK_STATUS_META[s].label}
                  </button>
                ))}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'Inter', fontSize: 14, color: C.icy, fontWeight: 500,
                              textDecoration: t.status === 'done' ? 'line-through' : 'none',
                              opacity: t.status === 'done' ? 0.65 : 1 }}>
                  {t.title}
                </div>
                {t.description && (
                  <div style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.55 }}>
                    {t.description.length > 220 ? t.description.slice(0, 220) + '…' : t.description}
                  </div>
                )}
                {t.source === 'line_item' && (
                  <div style={{ fontFamily: 'Inter', fontSize: 10, color: C.faint, marginTop: 4, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    From line item · {t.source_leak_area || '—'}
                  </div>
                )}
              </div>
              <button data-testid={`task-${t.id}-delete`} onClick={() => deleteTask(t.id)}
                      style={{ color: C.faint, background: 'transparent', border: 'none',
                                fontFamily: 'Inter', fontSize: 12, cursor: 'pointer' }}>
                Remove
              </button>
            </div>
          ))}
        </div>

        {/* Add task input */}
        <div className="mt-5 flex gap-2">
          <input data-testid="task-add-input" value={newTask} onChange={(e) => setNewTask(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }}
                 placeholder="Add a task — e.g. 'Confirm Twilio number for missed-call SMS'"
                 style={{
                   flex: 1, padding: '11px 14px', borderRadius: 12,
                   background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                   color: C.icy, fontFamily: 'Inter', fontSize: 14, outline: 'none',
                 }} />
          <button data-testid="task-add-submit" onClick={addTask} disabled={busy || !newTask.trim()}
                  style={{
                    padding: '11px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: C.spectrum, color: '#0F0824',
                    fontFamily: 'Inter', fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
                    opacity: busy || !newTask.trim() ? 0.5 : 1,
                  }}>
            Add task
          </button>
        </div>
      </section>

      {/* Placeholder lanes — schema present, UI coming with client portal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <PlaceholderLane
          title="Concerns / fault reports"
          subtitle="Client-submitted issues will surface here once the client portal lands."
          count={(project.concerns || []).filter((c) => c.status === 'open' || c.status === 'in_progress').length}
          testid="lane-concerns" />
        <PlaceholderLane
          title="Upgrade requests"
          subtitle="Future quote opportunities the client raises themselves — wired in with the client portal."
          count={(project.upgrade_requests || []).filter((u) => u.status === 'submitted').length}
          testid="lane-upgrades" />
      </div>

      {/* Project link-out row */}
      <div className="flex flex-wrap items-center gap-5" style={{ color: C.faint, fontFamily: 'Inter', fontSize: 12 }}>
        {project.session_id && (
          <Link to={`/admin/os/sessions/${project.session_id}`} style={{ color: C.muted }}>
            ← Linked discovery
          </Link>
        )}
        {project.quote_id && <span>Quote · {project.quote_id}</span>}
        <span>Client ID · {project.client_id}</span>
        {project.kicked_off_at && <span>Kickoff · {fmtDate(project.kicked_off_at)}</span>}
        {project.delivered_at && <span>Delivered · {fmtDate(project.delivered_at)}</span>}
      </div>
    </V2OSChrome>
  );
};

const PlaceholderLane = ({ title, subtitle, count, testid }) => (
  <div data-testid={testid} style={{
    padding: '18px 20px', borderRadius: 16,
    background: 'rgba(220,240,245,0.02)', border: `1px dashed ${C.hair}`,
  }}>
    <div className="flex items-center justify-between">
      <h3 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 15, color: C.icy }}>{title}</h3>
      <span style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {count} open
      </span>
    </div>
    <p style={{ fontFamily: 'Inter', fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.55 }}>
      {subtitle}
    </p>
    <p style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, marginTop: 8, letterSpacing: '0.06em' }}>
      Schema ready · Client portal pending build
    </p>
  </div>
);

// ─── Xero status badge (list-view header) ──────────────────────────────────
const XeroStatusBadge = ({ status, onConnect }) => {
  if (!status) return null;
  if (!status.configured) {
    return (
      <div data-testid="xero-badge-not-configured"
           style={{ padding: '10px 14px', borderRadius: 14, background: 'rgba(235,37,141,0.06)',
                    border: '1px dashed rgba(235,37,141,0.45)', fontFamily: 'Inter', fontSize: 12.5,
                    color: '#EB258D', maxWidth: 360, lineHeight: 1.5 }}>
        <strong style={{ fontWeight: 700, letterSpacing: '0.06em' }}>XERO · Not configured</strong>
        <div style={{ color: C.muted, marginTop: 4 }}>
          Backend env vars missing. See deploy runbook to set XERO_CLIENT_ID / XERO_CLIENT_SECRET.
        </div>
      </div>
    );
  }
  if (!status.connected) {
    return (
      <button data-testid="xero-connect-button" onClick={onConnect}
              style={{ padding: '12px 20px', borderRadius: 999, cursor: 'pointer', background: C.spectrum,
                       color: '#0F0824', fontFamily: 'Inter', fontSize: 13, fontWeight: 700,
                       letterSpacing: '0.04em', border: 'none' }}>
        Connect Xero →
      </button>
    );
  }
  return (
    <div data-testid="xero-badge-connected"
         style={{ padding: '10px 14px', borderRadius: 14, background: 'rgba(52,209,176,0.06)',
                  border: '1px solid rgba(52,209,176,0.35)', fontFamily: 'Inter', fontSize: 12.5,
                  color: '#34D1B0', lineHeight: 1.5 }}>
      <strong style={{ fontWeight: 700, letterSpacing: '0.06em' }}>XERO · Connected</strong>
      <div style={{ color: C.muted, marginTop: 4 }}>{status.tenant_name || 'Organisation'}</div>
    </div>
  );
};

// ─── Xero invoice card on the project detail page ──────────────────────────
const XeroInvoiceCard = ({ project, busy, onSync, onRefresh }) => {
  const hasInvoice = !!project.xero_invoice_id;
  return (
    <section data-testid="project-xero-card" className="rounded-2xl mb-6"
             style={{ background: 'rgba(52,209,176,0.04)', border: `1px solid ${C.hair}`,
                       borderLeft: '3px solid rgba(52,209,176,0.55)', padding: '20px 22px' }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 11, letterSpacing: '0.20em',
                        textTransform: 'uppercase', color: 'rgba(52,209,176,0.85)' }}>
            Xero · Draft invoice
          </div>
          <div style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: C.icy, marginTop: 4 }}>
            {hasInvoice
              ? <>Invoice <span data-testid="project-xero-invoice-number">{project.xero_invoice_number || '—'}</span></>
              : 'Not yet pushed to Xero'}
          </div>
          {hasInvoice && (
            <div style={{ fontFamily: 'Inter', fontSize: 12.5, color: C.muted, marginTop: 4 }}>
              Xero status: <strong style={{ color: C.icy }}>{project.xero_invoice_status || '—'}</strong>
              {project.payment_status && project.payment_status !== 'unknown' && (
                <> · Payment: <strong style={{ color: project.payment_status === 'paid' ? '#34D1B0' : C.icy }}>{project.payment_status}</strong>
                  {project.paid_amount_aud > 0 && <> (paid ${project.paid_amount_aud.toFixed(2)})</>}
                </>
              )}
            </div>
          )}
          {project.xero_sync_error && (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#EB258D', marginTop: 6 }}>
              Last sync error: {project.xero_sync_error}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!hasInvoice && (
            <button data-testid="project-xero-sync" onClick={onSync} disabled={busy}
                    style={{ padding: '9px 18px', borderRadius: 999, cursor: 'pointer',
                              background: C.spectrum, color: '#0F0824', border: 'none',
                              fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em',
                              opacity: busy ? 0.6 : 1 }}>
              Push DRAFT to Xero
            </button>
          )}
          {hasInvoice && (
            <button data-testid="project-xero-refresh" onClick={onRefresh} disabled={busy}
                    style={{ padding: '9px 18px', borderRadius: 999, cursor: 'pointer',
                              background: 'transparent', color: C.icy,
                              border: `1px solid ${C.hairBright}`,
                              fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600, letterSpacing: '0.04em',
                              opacity: busy ? 0.6 : 1 }}>
              Refresh from Xero
            </button>
          )}
        </div>
      </div>
      <div style={{ fontFamily: 'Inter', fontSize: 11.5, color: C.faint, lineHeight: 1.55 }}>
        Always DRAFT — Michael reviews and sends every invoice from Xero himself. We never auto-send.
        {project.xero_last_synced_at && (
          <> · Last synced {fmtDate(project.xero_last_synced_at)}</>
        )}
      </div>
    </section>
  );
};
